<template>
  <div class="chart-root" data-testid="month-chart-root">
    <div class="blocking-loader" v-if="loading">
      <AlLoading />
    </div>
    <highcharts :options="options" />
  </div>
</template>

<script>
import { Chart } from "highcharts-vue";
import { Translator, debounce } from "@agyletime/wfm-vue-common";
import { getDaysInMonth } from "date-fns";
import { prop, map, propOr, pipe, zipWith, add, subtract } from "ramda";
import { AlLoading } from "@playvox/almendra-components";
import { roundToNearestMonth } from "@agyletime/date-utils";

export default {
  name: "MonthChart",
  mixins: [Translator],
  components: {
    highcharts: Chart,
    AlLoading,
  },
  props: {
    monthData: {
      type: Array,
      required: true,
    },
    month: {
      type: Date,
      required: true,
    },
    unitOfWork: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  watch: {
    monthData() {
      this.setChartData(this.monthData);
    },
  },
  created() {
    this.setChartData(this.monthData);
  },
  data() {
    return {
      size: this.getScreenSize(),
      available: [],
      required: [],
      shrinkage: [],
      leave: [],
    };
  },
  computed: {
    title() {
      return this.getValueInLocale(roundToNearestMonth({ date: this.month }), {
        month: "long",
      });
    },
    daysInMonth() {
      return getDaysInMonth(roundToNearestMonth({ date: this.month })) - 1;
    },
    step() {
      if (this.size < 576) {
        return 3;
      } else if (this.size < 992) {
        return 2;
      } else {
        return 1;
      }
    },
    series() {
      const leaveTotalled = zipWith(
        add,
        this.unplannedLeave,
        this.plannedLeave
      );
      const available = zipWith(
        subtract,
        this.available,
        zipWith(add, this.shrinkage, leaveTotalled)
      );
      return [
        {
          name: "Available",
          data: available,
          color: "var(--color-green-pantone-40)",
          opacity: 0.5,
          zIndex: 3,
        },
        {
          name: "Required",
          data: this.required,
          color: "var(--color-maize-crayola-40)",
          type: "line",
          marker: {
            enabled: false,
          },
          zIndex: 4,
        },
        {
          name: "Shrinkage",
          data: this.shrinkage,
          color: "var(--color-deep-violet-40)",
          zIndex: 2,
        },
        {
          name: "Leave",
          data: leaveTotalled,
          color: "var(--color-sky-blue-40)",
          zIndex: 1,
        },
      ];
    },
    options() {
      return {
        credits: { enabled: false },
        chart: {
          type: "area",
        },
        title: {
          align: "left",
          text: this.title,
          x: -10,
        },
        legend: {
          align: "left",
        },
        xAxis: {
          min: 0,
          max: this.daysInMonth,
          labels: {
            formatter: function () {
              return this.pos + 1;
            },
            step: this.step,
          },
          tickInterval: 1,
          tickLength: 0,
        },
        yAxis: {
          title: {
            text: this.unitOfWork,
          },
          reversedStacks: false,
        },
        tooltip: {
          headerFormat: "",
          pointFormat: "{series.name}: {point.y:.1f}",
        },
        series: this.series,
        plotOptions: {
          series: {
            dataLabels: {
              formatter: function () {
                if (this.y > 0) {
                  return this.y;
                }
              },
            },
          },
          area: {
            stacking: "normal",
            lineWidth: 0,
            marker: {
              enabled: false,
            },
          },
        },
      };
    },
  },
  methods: {
    setChartData(data) {
      const unitOfWorkProp = propOr(0, this.unitOfWork.toLowerCase());

      this.available = map(pipe(prop("available"), unitOfWorkProp))(data);
      this.required = map(pipe(prop("required"), unitOfWorkProp))(data);
      this.shrinkage = map(pipe(prop("shrinkage"), unitOfWorkProp))(data);
      this.plannedLeave = map(pipe(prop("plannedLeave"), unitOfWorkProp))(data);
      this.unplannedLeave = map(pipe(prop("unplannedLeave"), unitOfWorkProp))(
        data
      );
    },
    setScreenSize: debounce(function () {
      this.size = this.getScreenSize();
    }),
    getScreenSize() {
      return window.innerWidth;
    },
  },

  mounted() {
    window.addEventListener("resize", this.setScreenSize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.setScreenSize);
  },
};
</script>

<style scoped lang="scss">
.chart-root {
  position: relative;
}
.blocking-loader {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
}
</style>
